/**
 * board-utils — pure helpers for the dispatch board (Wave 3 Part 2 / Quick 260615-uf9).
 * Grouping, ordering, and metric computation. No React; unit-testable.
 */

import type { DispatchJob } from '../jobs/use-jobs';
import { statusFullLabel } from '../jobs/job-status.js';

/** In-progress (work started, not terminal) statuses — mirrors DriverStack (Wave 2 #5). */
export const IN_PROGRESS_STATUSES: ReadonlySet<string> = new Set([
  'EN_ROUTE_TO_PICKUP',
  'ARRIVED_PICKUP',
  'ONSITE_WORK_START',
  'ONSITE_WORK_END',
  'EN_ROUTE_TO_DROP_OFF',
  'DISPOSAL_IN',
  'UNLOADING_START',
  'DISPOSAL_OUT',
]);

export const TERMINAL_STATUSES: ReadonlySet<string> = new Set([
  'COMPLETED',
  'CANCELLED',
  'REJECTED',
  'INVOICED',
]);

/**
 * Statuses hidden from dispatch-board driver cards (field-test fix). Once a job
 * leaves the active work chain — post-completion review (IN_REVIEW), billing
 * (TO_BE_INVOICED / INVOICED), or a closed state (COMPLETED / CANCELLED /
 * REJECTED) — the driver is no longer working it, so it must not clutter the
 * card. The `/v1/jobs` default feed only excludes COMPLETED + CANCELLED, so the
 * review/billing states leaked onto the cards; this is the client-side guard.
 *
 * PENDING and the in-flight chain stay visible. PAUSED jobs also stay: a paused
 * job keeps its underlying active status_code (§11 — PAUSED is a clock state,
 * not a status_code), so it is never filtered out here.
 */
export const DRIVER_CARD_HIDDEN_STATUSES: ReadonlySet<string> = new Set([
  'IN_REVIEW',
  'TO_BE_INVOICED',
  'COMPLETED',
  'INVOICED',
  'CANCELLED',
  'REJECTED',
]);

export function isInProgress(job: DispatchJob): boolean {
  return IN_PROGRESS_STATUSES.has(job.statusCode);
}

/** True when a job should appear on a driver's dispatch-board card (active work only). */
export function isActiveDriverJob(job: DispatchJob): boolean {
  return !DRIVER_CARD_HIDDEN_STATUSES.has(job.statusCode);
}

// ── Task #32 — status icons, hover text, off-shift filter ───────────────────

/**
 * The status icon on a driver-card job row (task #32). The words came off the row; the
 * hover / accessible text (jobRowSummary) carries the real status.
 *   underway   — any in-progress status (driving, on site, loading, at disposal): green dot
 *   assigned   — PENDING (assigned, not started): no dot
 *   other      — a code the board does not know: gray ring, so it is never mistaken for either
 */
export type JobRowStatusIcon = 'underway' | 'assigned' | 'other';

export function jobRowStatusIcon(statusCode: string): JobRowStatusIcon {
  if (IN_PROGRESS_STATUSES.has(statusCode)) return 'underway';
  if (statusCode === 'PENDING') return 'assigned';
  return 'other';
}

/**
 * Hover + accessible text for a job row: job type and the ACTUAL status, e.g.
 * "Produced Water Haul · En route to pickup". Never a generic "Underway".
 */
export function jobRowSummary(job: Pick<DispatchJob, 'jobTypeLabel' | 'statusCode'>): string {
  const status = statusFullLabel(job.statusCode);
  return job.jobTypeLabel !== '' ? `${job.jobTypeLabel} · ${status}` : status;
}

/**
 * Task #32 — which drivers the board shows. On-shift drivers always; off-shift ones only
 * when the viewer asks, EXCEPT an off-shift driver who still has jobs on their card, who is
 * always shown (their work must never disappear from the board).
 *
 * OPEN-027: only `hasOpenShift === false` means off shift. An absent value (an older
 * payload) is UNKNOWN and is treated as shown, never as off shift.
 */
export function boardVisibleDrivers<D extends { driverId: string; hasOpenShift?: boolean }>(
  drivers: readonly D[],
  jobsByDriver: ReadonlyMap<string, readonly unknown[]>,
  showOffShift: boolean,
): D[] {
  return drivers.filter(
    (d) =>
      showOffShift || d.hasOpenShift !== false || (jobsByDriver.get(d.driverId)?.length ?? 0) > 0,
  );
}

// ── Quick 260616-f4r ─────────────────────────────────────────────────────────

/** #5 — scheduled-time urgency for a job card. `none` = no scheduled time. */
export type JobUrgency = 'overdue' | 'due-soon' | 'on-track' | 'none';

const DUE_SOON_MS = 2 * 60 * 60 * 1000; // within 2h → due soon

export function jobUrgency(scheduledAt: string | null, nowMs: number = Date.now()): JobUrgency {
  if (scheduledAt === null || scheduledAt === '') return 'none';
  const t = new Date(scheduledAt).getTime();
  if (Number.isNaN(t)) return 'none';
  if (t < nowMs) return 'overdue';
  if (t - nowMs <= DUE_SOON_MS) return 'due-soon';
  return 'on-track';
}

/** #3 — driver availability for the card status dot. offline > busy > available. */
export type DriverAvailability = 'available' | 'busy' | 'offline';

const OFFLINE_MS = 2 * 60 * 60 * 1000; // no device event in >2h → offline

export function driverAvailability(
  hasActiveJob: boolean,
  lastEventAt: string | null,
  nowMs: number = Date.now(),
): DriverAvailability {
  if (lastEventAt !== null && lastEventAt !== '') {
    const t = new Date(lastEventAt).getTime();
    if (!Number.isNaN(t) && nowMs - t > OFFLINE_MS) return 'offline';
  }
  return hasActiveJob ? 'busy' : 'available';
}

/** #4 — humanize an elapsed-ms duration as "2h 16m" / "45m". */
export function humanizeAgo(ms: number): string {
  const totalMin = Math.max(0, Math.floor(ms / 60000));
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return h > 0 ? `${h.toString()}h ${m.toString()}m` : `${m.toString()}m`;
}

/**
 * #4 — tooltip text for a driver's sync badge / availability dot.
 * Synced/recent → "Last synced: Jun 16, 11:31 AM".
 * Offline (>2h) → "Last seen: Jun 16, 9:15 AM (2h 16m ago)".
 */
export function formatSyncTooltip(
  lastEventAt: string | null,
  nowMs: number,
  timeZone: string,
): string {
  if (lastEventAt === null || lastEventAt === '') return 'No sync recorded yet';
  const t = new Date(lastEventAt).getTime();
  if (Number.isNaN(t)) return 'No sync recorded yet';
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone,
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(t));
  const ageMs = nowMs - t;
  return ageMs > OFFLINE_MS
    ? `Last seen: ${fmt} (${humanizeAgo(ageMs)} ago)`
    : `Last synced: ${fmt}`;
}

/**
 * #46 / DEC-361 — the shared job order's pinned bands, the same as the API's
 * (packages/api/src/services/job-order.ts) and the driver's My Jobs:
 *   0  the GOING job (clock head STARTED or RESUMED)
 *   1  urgent jobs
 *   2  everything else
 */
export type JobOrderBand = 0 | 1 | 2;

export function jobOrderBand(job: DispatchJob): JobOrderBand {
  if (job.clockState === 'STARTED' || job.clockState === 'RESUMED') return 0;
  if (job.isUrgent === true) return 1;
  return 2;
}

/**
 * Order a driver's jobs for display (#46 / DEC-361): band first, then the shared
 * dispatcher_sort_order (NULLS LAST), then newest first, then id — exactly GET /v1/jobs'
 * order, so the board, the API and the phone agree. (Was: in-progress statuses first.)
 */
export function sortDriverJobs(jobs: readonly DispatchJob[]): DispatchJob[] {
  return [...jobs].sort((a, b) => {
    const ab = jobOrderBand(a);
    const bb = jobOrderBand(b);
    if (ab !== bb) return ab - bb;
    const ao = a.dispatcherSortOrder ?? Number.MAX_SAFE_INTEGER;
    const bo = b.dispatcherSortOrder ?? Number.MAX_SAFE_INTEGER;
    if (ao !== bo) return ao - bo;
    if (a.createdAt !== b.createdAt) return b.createdAt.localeCompare(a.createdAt);
    return a.id.localeCompare(b.id);
  });
}

/**
 * #46 / DEC-361 — a drag only reorders within the moved job's band. A drop past the band
 * edge is clamped to it instead of refused: dropping an urgent job among the normal jobs
 * puts it LAST among the urgent ones, and dropping a normal job above the urgent ones puts
 * it FIRST among the normal ones. `column` is the target column in display order
 * (sortDriverJobs); returns the beforeJobId to send (null = end of the column).
 */
export function clampDropToBand(
  column: readonly DispatchJob[],
  moved: DispatchJob,
  beforeJobId: string | null,
): string | null {
  const band = jobOrderBand(moved);
  const others = column.filter((j) => j.id !== moved.id);
  const anchor = beforeJobId === null ? undefined : others.find((j) => j.id === beforeJobId);
  if (anchor !== undefined && jobOrderBand(anchor) === band) return anchor.id;
  if (anchor !== undefined && jobOrderBand(anchor) < band) {
    // Above its band: the first job of its own band (or of a later band).
    return others.find((j) => jobOrderBand(j) >= band)?.id ?? null;
  }
  // Below its band, or the end of the column: just before the next band.
  return others.find((j) => jobOrderBand(j) > band)?.id ?? null;
}

/** Jobs with no driver (non-terminal) — the Unassigned panel source. */
export function unassignedJobs(jobs: readonly DispatchJob[]): DispatchJob[] {
  return jobs.filter((j) => j.driverId === null && !TERMINAL_STATUSES.has(j.statusCode));
}

export interface SiteGroup {
  site: string;
  jobs: DispatchJob[];
}
export interface OperatorGroup {
  operator: string;
  count: number;
  sites: SiteGroup[];
}

/**
 * Group unassigned jobs by operator, then by site within each operator (Quick
 * 260616-e14 #4). Operators + sites sorted alphabetically; jobs within a site by age.
 */
export function groupByOperatorSite(jobs: readonly DispatchJob[]): OperatorGroup[] {
  const ops = new Map<string, Map<string, DispatchJob[]>>();
  for (const j of jobs) {
    const op = j.operatorName || 'No operator';
    const site = j.siteName || 'No site';
    const sites = ops.get(op) ?? new Map<string, DispatchJob[]>();
    const arr = sites.get(site) ?? [];
    arr.push(j);
    sites.set(site, arr);
    ops.set(op, sites);
  }
  return [...ops.entries()]
    .map(([operator, sites]) => ({
      operator,
      count: [...sites.values()].reduce((n, a) => n + a.length, 0),
      sites: [...sites.entries()]
        .map(([site, list]) => ({
          site,
          jobs: [...list].sort((a, b) => a.createdAt.localeCompare(b.createdAt)),
        }))
        .sort((a, b) => a.site.localeCompare(b.site)),
    }))
    .sort((a, b) => a.operator.localeCompare(b.operator));
}

/** Group unassigned jobs by site name (alphabetical), each group sorted by age. */
export function groupBySite(
  jobs: readonly DispatchJob[],
): Array<{ site: string; jobs: DispatchJob[] }> {
  const map = new Map<string, DispatchJob[]>();
  for (const j of jobs) {
    const site = j.siteName || 'No site';
    const arr = map.get(site) ?? [];
    arr.push(j);
    map.set(site, arr);
  }
  return [...map.entries()]
    .map(([site, list]) => ({
      site,
      jobs: [...list].sort((a, b) => a.createdAt.localeCompare(b.createdAt)),
    }))
    .sort((a, b) => a.site.localeCompare(b.site));
}

export interface BoardMetrics {
  unassigned: number;
  notStarted: number;
  inProgress: number;
  completedToday: number;
}

/**
 * Metrics for the top bar. `active` = the default jobs list (non-terminal).
 *
 * OPEN-057 — `completedToday` is now a COUNT supplied by the server
 * (GET /v1/jobs/completed/count), not something derived here from a page of
 * jobs. Deriving it client-side could not be correct: the page was ordered by
 * `created_at DESC` rather than `completed_at`, so a job created weeks ago and
 * completed today fell outside it silently; and it filtered on the transient
 * `COMPLETED` status, so a job completed this morning and advanced to
 * TO_BE_INVOICED this afternoon disappeared from the tile.
 */
export function computeMetrics(
  active: readonly DispatchJob[],
  completedToday: number,
): BoardMetrics {
  let unassigned = 0;
  let notStarted = 0;
  let inProgress = 0;
  for (const j of active) {
    if (TERMINAL_STATUSES.has(j.statusCode)) continue;
    if (j.driverId === null) {
      unassigned += 1;
    } else if (IN_PROGRESS_STATUSES.has(j.statusCode)) {
      inProgress += 1;
    } else {
      notStarted += 1;
    }
  }
  return { unassigned, notStarted, inProgress, completedToday };
}
